import { useCallback } from "react";
import { getPRD5040_R01List } from "./Api_PRD5040_R01";
import { usePRD5040_R01Store } from "./Store_PRD5040_R01";
import { PRD5040_R01ResultDto } from "./Dto_PRD5040_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

// Content 컴포넌트(훅)가 받는 props 타입
interface PRD5040_R01ContentProps {
	setMessage: (message: string) => void; // 하단 Message 컴포넌트에 표시할 메시지 세터
}

// 조회 로직(비즈니스 로직)만 분리해 담당하는 훅 형태의 컴포넌트
export const Content_PRD5040_R01 = (props: PRD5040_R01ContentProps) => {
	const { setMessage } = props; // 메시지 세터 추출

	// 스토어에서 현재 필터값과, 결과 리스트를 갱신하는 함수를 가져옴
	const {
		PRD5040_R01Filter,
		setPRD5040_R01List,
	} = usePRD5040_R01Store();

	// 전역 로딩 스피너 표시/숨김 제어 함수
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	// 액션바(조회/저장/삭제 등) 버튼별 사용 권한을 메뉴코드 기준으로 조회/적용하는 훅
	useFetchActionbarAuth("PRD5040_R01");

	// 실제 조회를 수행하는 콜백 함수 (필터가 바뀌면 재생성됨)
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true); // 조회 시작 - 로딩 스피너 on
		try {
			// 현재 필터 조건으로 백엔드 API 호출
			const response = await getPRD5040_R01List('', PRD5040_R01Filter);
			// 응답 데이터가 없을 경우를 대비해 빈 배열로 폴백
			const newList = [...(response.data.data ?? [])] as PRD5040_R01ResultDto[];

			setPRD5040_R01List(newList); // 조회 결과를 스토어에 반영

			// 월별 12행은 항상 내려오므로, 실데이터 유무는 생산수량/불량수량 합으로 판단
			const hasData = newList.some((d) => Number(d.productQty) > 0 || Number(d.badQty) > 0);
			if (hasData) {
				// 데이터가 있으면 성공 토스트 표시
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (${PRD5040_R01Filter.year}년)`);
			} else {
				// 데이터가 전혀 없으면 경고 토스트 표시
				showWarning('데이터가 없습니다.');
			}
		} catch (error) {
			// 조회 중 예외 발생 시 콘솔 로그 + 경고 토스트 + 리스트 비우기
			console.error('데이터 조회 실패:', error);
			showWarning('데이터 조회에 실패했습니다.');
			setPRD5040_R01List([]);
		} finally {
			setLoadingSpinner(false); // 조회 종료 - 로딩 스피너 off (성공/실패 모두)
		}
	}, [PRD5040_R01Filter, setPRD5040_R01List, setMessage, setLoadingSpinner]);

	// Container에서 사용할 수 있도록 fetchData 함수를 반환
	return {
		fetchData,
	};
};
